/**
 * Utility functions for handling hash-based URLs
 */

/**
 * Check if the current URL has a hash (#) present
 * @returns boolean indicating if hash is present
 */
export const hasHashInUrl = (): boolean => {
  return window.location.hash !== '';
};

/**
 * Get the hash portion of the URL (without the # symbol)
 * @returns string - the hash path (e.g., '/payment/featured-store/hyperpay/success')
 */
export const getHashPath = (): string => {
  const hash = window.location.hash;
  if (!hash) return '';
  
  // Remove the leading # and extract just the path (before query string)
  const hashWithoutSymbol = hash.substring(1);
  const [path] = hashWithoutSymbol.split('?');
  return path;
};

/**
 * Get query parameters from hash-based URL
 * Works with both formats:
 * - #/path?param=value
 * - /path?param=value (before redirect)
 * @returns URLSearchParams object
 */
export const getHashQueryParams = (): URLSearchParams => {
  const hash = window.location.hash;
  
  if (hash && hash.includes('?')) {
    // Hash-based URL: #/path?param=value
    const [, queryString] = hash.split('?');
    return new URLSearchParams(queryString);
  } else if (window.location.search) {
    // Direct URL: /path?param=value
    return new URLSearchParams(window.location.search);
  }
  
  return new URLSearchParams();
};

/**
 * Get a specific query parameter from the URL
 * @param paramName - name of the query parameter
 * @returns string value or null if not found
 */
export const getHashQueryParam = (paramName: string): string | null => {
  const params = getHashQueryParams();
  return params.get(paramName);
};

/**
 * Get all query parameters as an object
 * @returns Record<string, string> - object with all query parameters
 */
export const getAllHashQueryParams = (): Record<string, string> => {
  const params = getHashQueryParams();
  const result: Record<string, string> = {};
  
  params.forEach((value, key) => {
    result[key] = value;
  });
  
  return result;
};

/**
 * Check if the current URL needs to be converted to hash format
 * This is useful for detecting external redirects (e.g., from payment providers)
 * @returns boolean - true if URL needs hash conversion
 */
export const needsHashConversion = (): boolean => {
  const path = window.location.pathname;
  const hash = window.location.hash;
  
  // If we have a meaningful path and no hash, we need conversion
  return path !== '/' && (!hash || hash === '#');
};

/**
 * Convert current URL to hash-based format
 * Useful for manual redirects when detecting external URLs
 */
export const convertToHashUrl = (): string => {
  const path = window.location.pathname;
  const search = window.location.search;
  return `/#${path}${search}`;
};

/**
 * Get the full URL info for debugging
 * @returns object with URL components
 */
export const getUrlInfo = () => {
  return {
    href: window.location.href,
    pathname: window.location.pathname,
    hash: window.location.hash,
    search: window.location.search,
    hasHash: hasHashInUrl(),
    hashPath: getHashPath(),
    queryParams: getAllHashQueryParams(),
    needsConversion: needsHashConversion()
  };
};

